import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  getAllCompliances, updateCompliance, deleteCompliance,
  uploadComplianceFile, deleteComplianceFile,
  getClients, getTasks, getComplianceTypes, triggerComplianceCron,
  bulkUpdateCompliances, triggerGstFilingCheck, uploadsUrl,
} from '../api';
import { useAuth } from '../contexts/AuthContext';
import ColumnFilter from '../components/ColumnFilter';
import DateRangeFilter, { inDateRange } from '../components/DateRangeFilter';
import { exportToExcel, fmtDate as fmtDateExcel } from '../utils/exportExcel';
import BulkEnrollComplianceWizard from '../components/BulkEnrollComplianceWizard';
import { formatDateDdMmmYy } from '../utils/format';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

interface CType { id: number; name: string; frequency: string; }
interface CTask { id: number; taskId: string; taskName: string; client: { id: number; clientName: string }; }
interface ComplianceItem {
  id: number;
  complianceNumber: string;
  taskId: number;
  task: CTask;
  complianceType: CType;
  taxId?: string | null;
  periodLabel: string;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  escalationDate: string;
  extendedDueDate?: string;
  status: string;
  completedOn?: string;
  remarks?: string;
  fileName?: string;
  originalName?: string;
  fileSize?: number;
  mimeType?: string;
  gstFilingStatus?: string;
  gstFiledDate?: string;
  gstEscalationLevel?: string;
  gstLastCheckedAt?: string;
}

const STATUS_COLORS: Record<string, string> = {
  PENDING:     'bg-yellow-100 text-yellow-800 border-yellow-200',
  IN_PROGRESS: 'bg-blue-100 text-blue-800 border-blue-200',
  COMPLETED:   'bg-green-100 text-green-700 border-green-200',
  OVERDUE:     'bg-red-100 text-red-800 border-red-200',
};

const ESCALATION_COLORS: Record<string, string> = {
  ESCALATION:           'bg-amber-100 text-amber-800 border-amber-200',
  IMMEDIATE_ATTENTION:  'bg-orange-100 text-orange-800 border-orange-200',
  OVERDUE:              'bg-red-100 text-red-800 border-red-200',
};

const ESCALATION_LABELS: Record<string, string> = {
  ESCALATION:           'Escalation',
  IMMEDIATE_ATTENTION:  'Immediate Attention',
  OVERDUE:              'Overdue',
};

const GST_TYPE_NAMES = ['GSTR-1', 'GSTR-3B', 'GSTR-6'];

const fmtDate = (d?: string) => formatDateDdMmmYy(d) || '—';
const fmtSize = (bytes?: number) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Compliances: React.FC = () => {
  const { isAdmin, isHR, isPartner } = useAuth();
  const canEdit = isAdmin || isHR || isPartner;

  const [items, setItems] = useState<ComplianceItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [filterClientId, setFilterClientId] = useState('');
  const [filterTaskId, setFilterTaskId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTypeId, setFilterTypeId] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('');
  const [dueFrom, setDueFrom] = useState('');
  const [dueTo, setDueTo] = useState('');

  const [clients, setClients] = useState<{ id: number; clientName: string }[]>([]);
  const [tasks, setTasks] = useState<{ id: number; taskId: string; taskName: string; clientId?: number }[]>([]);
  const [types, setTypes] = useState<{ id: number; name: string }[]>([]);

  // Inline editing
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editRemarks, setEditRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState<number | null>(null);
  const [generating, setGenerating] = useState(false);

  // Multi-select
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkAction, setBulkAction] = useState('');

  // Due date extension (single)
  const [extendingId, setExtendingId] = useState<number | null>(null);
  const [extendDate, setExtendDate] = useState('');

  // Bulk extend modal
  const [showBulkExtend, setShowBulkExtend] = useState(false);
  const [showBulkEnroll, setShowBulkEnroll] = useState(false);
  const [bulkExtendTypeId, setBulkExtendTypeId] = useState('');
  const [bulkExtendPeriod, setBulkExtendPeriod] = useState('');
  const [bulkExtendDate, setBulkExtendDate] = useState('');
  const [gstChecking, setGstChecking] = useState(false);

  // GST filing check chooser (which return types + which periods)
  const [showGstCheck, setShowGstCheck] = useState(false);
  const [gstCheckTypes, setGstCheckTypes] = useState<string[]>(GST_TYPE_NAMES);
  const [gstCheckPeriods, setGstCheckPeriods] = useState<string[]>([]);

  // Sorting
  const [sortField, setSortField] = useState<'complianceNumber' | 'type' | 'task' | 'client' | 'period' | 'dueDate' | 'status' | 'gstFilingStatus'>('complianceNumber');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const handleSort = (f: typeof sortField) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
  };
  const SI = ({ f }: { f: typeof sortField }) =>
    sortField !== f ? <span className="ml-1 text-gray-300">↕</span>
    : <span className="ml-1 text-blue-600">{sortDir === 'asc' ? '↑' : '↓'}</span>;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {};
      if (search) params.search = search;
      if (filterClientId) params.clientId = filterClientId;
      if (filterTaskId) params.taskId = filterTaskId;
      if (filterStatus) params.status = filterStatus;
      if (filterTypeId) params.complianceTypeId = filterTypeId;
      const res = await getAllCompliances(params);
      setItems(res.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [search, filterClientId, filterTaskId, filterStatus, filterTypeId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getClients().then((r) => setClients(r.data)).catch(() => {});
    getTasks().then((r) => setTasks(r.data)).catch(() => {});
    getComplianceTypes().then((r) => setTypes(r.data)).catch(() => {});
  }, []);

  // Derived: unique periods from loaded data
  const periodOptions = useMemo(() => {
    const set = new Set(items.map(i => i.periodLabel));
    return Array.from(set).sort().map(p => ({ value: p, label: p }));
  }, [items]);

  // Derived: unique periods that actually have GST-type compliances
  const gstPeriodOptions = useMemo(() => {
    const set = new Set(items.filter(i => GST_TYPE_NAMES.includes(i.complianceType.name)).map(i => i.periodLabel));
    return Array.from(set).sort();
  }, [items]);

  // How many pending GST compliances match the current type+period selection
  const gstMatchCount = useMemo(() => items.filter(i =>
    GST_TYPE_NAMES.includes(i.complianceType.name) &&
    i.status !== 'COMPLETED' &&
    i.gstFilingStatus !== 'Filed' &&
    gstCheckTypes.includes(i.complianceType.name) &&
    gstCheckPeriods.includes(i.periodLabel)
  ).length, [items, gstCheckTypes, gstCheckPeriods]);

  // Client-side period filter + sort
  const filteredItems = useMemo(() => {
    const list = items.filter(i => {
      if (filterPeriod && i.periodLabel !== filterPeriod) return false;
      if (dueFrom || dueTo) {
        const effective = i.extendedDueDate || i.dueDate;
        if (!inDateRange(effective, dueFrom, dueTo)) return false;
      }
      return true;
    });
    return [...list].sort((a, b) => {
      let av = '', bv = '';
      if (sortField === 'complianceNumber') { av = a.complianceNumber; bv = b.complianceNumber; }
      else if (sortField === 'type') { av = a.complianceType.name; bv = b.complianceType.name; }
      else if (sortField === 'task') { av = a.task.taskId; bv = b.task.taskId; }
      else if (sortField === 'client') { av = a.task.client.clientName; bv = b.task.client.clientName; }
      else if (sortField === 'period') { av = a.periodLabel; bv = b.periodLabel; }
      else if (sortField === 'dueDate') { av = a.extendedDueDate || a.dueDate || ''; bv = b.extendedDueDate || b.dueDate || ''; }
      else if (sortField === 'status') { av = a.status; bv = b.status; }
      else if (sortField === 'gstFilingStatus') { av = a.gstFilingStatus || ''; bv = b.gstFilingStatus || ''; }
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [items, filterPeriod, dueFrom, dueTo, sortField, sortDir]);

  const handleStatusChange = async (item: ComplianceItem, newStatus: string) => {
    try {
      await updateCompliance(item.id, { status: newStatus });
      load();
    } catch (err: any) { alert(err.response?.data?.message || 'Error'); }
  };

  const handleSaveRemarks = async (item: ComplianceItem) => {
    setSaving(true);
    try {
      await updateCompliance(item.id, { remarks: editRemarks || null });
      setEditingId(null);
      load();
    } catch (err: any) { alert(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (item: ComplianceItem) => {
    if (!window.confirm(`Delete ${item.complianceNumber}?`)) return;
    try { await deleteCompliance(item.id); load(); }
    catch (err: any) { alert(err.response?.data?.message || 'Error'); }
  };

  const handleFileUpload = async (item: ComplianceItem, file: File) => {
    setUploadingId(item.id);
    try { await uploadComplianceFile(item.id, file); load(); }
    catch (err: any) { alert(err.response?.data?.message || 'Upload failed'); }
    finally { setUploadingId(null); }
  };

  const handleDeleteFile = async (item: ComplianceItem) => {
    if (!window.confirm('Remove uploaded file?')) return;
    try { await deleteComplianceFile(item.id); load(); }
    catch (err: any) { alert(err.response?.data?.message || 'Error'); }
  };

  const handleGenerate = async () => {
    if (!window.confirm('Generate compliance instances for the last period?')) return;
    setGenerating(true);
    try { await triggerComplianceCron(); load(); }
    catch (err: any) { alert(err.response?.data?.message || 'Error'); }
    finally { setGenerating(false); }
  };

  const openGstCheck = () => {
    // Default: all return types that exist + all GST periods selected.
    setGstCheckTypes(GST_TYPE_NAMES.filter(n => items.some(i => i.complianceType.name === n)));
    setGstCheckPeriods(gstPeriodOptions);
    setShowGstCheck(true);
  };

  const handleGstCheck = async () => {
    if (gstCheckTypes.length === 0 || gstCheckPeriods.length === 0) return;
    setGstChecking(true);
    try {
      const res = await triggerGstFilingCheck({ typeNames: gstCheckTypes, periods: gstCheckPeriods });
      const d = res.data;
      alert(`GST Filing Check Complete\n\nChecked: ${d.checked}\nFiled: ${d.filed}\nNot Filed: ${d.notFiled}\nErrors: ${d.errors}`);
      setShowGstCheck(false);
      load();
    } catch (err: any) { alert(err.response?.data?.message || 'GST check failed'); }
    finally { setGstChecking(false); }
  };

  // Single extend
  const handleExtendSubmit = async () => {
    if (!extendingId || !extendDate) return;
    setSaving(true);
    try {
      await updateCompliance(extendingId, { extendedDueDate: extendDate });
      setExtendingId(null); setExtendDate('');
      load();
    } catch (err: any) { alert(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const handleClearExtension = async (item: ComplianceItem) => {
    if (!window.confirm('Remove due date extension?')) return;
    try { await updateCompliance(item.id, { extendedDueDate: null }); load(); }
    catch (err: any) { alert(err.response?.data?.message || 'Error'); }
  };

  // Bulk extend by type + period
  const handleBulkExtendSubmit = async () => {
    if (!bulkExtendTypeId || !bulkExtendPeriod || !bulkExtendDate) return;
    const matching = items.filter(i =>
      i.complianceType.id === Number(bulkExtendTypeId) && i.periodLabel === bulkExtendPeriod
    );
    if (matching.length === 0) { alert('No matching compliances found'); return; }
    if (!window.confirm(`Extend due date for ${matching.length} compliance(s) of this type and period to ${bulkExtendDate}?`)) return;
    setSaving(true);
    try {
      await bulkUpdateCompliances({
        ids: matching.map(i => i.id),
        action: 'EXTEND_DUE_DATE',
        extendedDueDate: bulkExtendDate,
      });
      setShowBulkExtend(false); setBulkExtendTypeId(''); setBulkExtendPeriod(''); setBulkExtendDate('');
      load();
    } catch (err: any) { alert(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  // Multi-select bulk operations
  const toggleSelect = (id: number) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredItems.length) setSelectedIds([]);
    else setSelectedIds(filteredItems.map(i => i.id));
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedIds.length === 0) return;
    if (bulkAction === 'DELETE') {
      if (!window.confirm(`Delete ${selectedIds.length} compliance(s)?`)) return;
      try {
        await bulkUpdateCompliances({ ids: selectedIds, action: 'DELETE' });
        setSelectedIds([]); setBulkAction(''); load();
      } catch (err: any) { alert(err.response?.data?.message || 'Error'); }
    } else {
      try {
        await bulkUpdateCompliances({ ids: selectedIds, action: 'STATUS_CHANGE', status: bulkAction });
        setSelectedIds([]); setBulkAction(''); load();
      } catch (err: any) { alert(err.response?.data?.message || 'Error'); }
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getEffectiveDue = (item: ComplianceItem) => item.extendedDueDate || item.dueDate;

  const getDueStatus = (item: ComplianceItem) => {
    if (item.status === 'COMPLETED') return null;
    const effectiveDue = new Date(getEffectiveDue(item));
    effectiveDue.setHours(0, 0, 0, 0);
    const escalation = new Date(effectiveDue);
    escalation.setDate(escalation.getDate() - 1);
    const diffDue = Math.ceil((effectiveDue.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDue < 0) return { label: `${Math.abs(diffDue)}d overdue`, cls: 'text-red-600 font-semibold' };
    if (today >= escalation) return { label: diffDue === 0 ? 'Due today' : `${diffDue}d left`, cls: 'text-red-600 font-semibold' };
    if (diffDue <= 3) return { label: `${diffDue}d left`, cls: 'text-amber-600 font-medium' };
    if (diffDue <= 7) return { label: `${diffDue}d left`, cls: 'text-yellow-600' };
    return null;
  };

  const isEscalated = (item: ComplianceItem) => {
    if (item.status === 'COMPLETED') return false;
    const effectiveDue = new Date(getEffectiveDue(item));
    effectiveDue.setHours(0, 0, 0, 0);
    const escalation = new Date(effectiveDue);
    escalation.setDate(escalation.getDate() - 1);
    return today >= escalation;
  };

  // Summary
  const pending = filteredItems.filter(i => i.status === 'PENDING').length;
  const overdue = filteredItems.filter(i => {
    if (i.status === 'COMPLETED') return false;
    const d = new Date(getEffectiveDue(i)); d.setHours(0,0,0,0);
    return d < today;
  }).length;
  const completed = filteredItems.filter(i => i.status === 'COMPLETED').length;

  // Filter options for ColumnFilter
  const typeOptions = useMemo(() => types.map(t => ({ value: String(t.id), label: t.name })), [types]);
  const clientOptions = useMemo(() => {
    const seen = new Set<number>();
    return items.reduce<{ value: string; label: string }[]>((acc, i) => {
      if (!seen.has(i.task.client.id)) {
        seen.add(i.task.client.id);
        acc.push({ value: String(i.task.client.id), label: i.task.client.clientName });
      }
      return acc;
    }, []).sort((a, b) => a.label.localeCompare(b.label));
  }, [items]);
  const taskOptions = useMemo(() => {
    const seen = new Set<number>();
    return items.reduce<{ value: string; label: string }[]>((acc, i) => {
      if (!seen.has(i.taskId)) {
        seen.add(i.taskId);
        acc.push({ value: String(i.taskId), label: `${i.task.taskId} – ${i.task.taskName}` });
      }
      return acc;
    }, []).sort((a, b) => a.label.localeCompare(b.label));
  }, [items]);

  const activeFilterCount = [filterClientId, filterTaskId, filterStatus, filterTypeId, filterPeriod, dueFrom || dueTo ? '1' : ''].filter(Boolean).length;

  return (
    <div className="max-w-full">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white rounded-xl border border-gray-200 p-3">
          <div className="text-2xl font-bold text-gray-900">{filteredItems.length}</div>
          <div className="text-xs text-gray-500">Total</div>
        </div>
        <div className="bg-white rounded-xl border border-yellow-200 p-3">
          <div className="text-2xl font-bold text-yellow-700">{pending}</div>
          <div className="text-xs text-yellow-600">Pending</div>
        </div>
        <div className="bg-white rounded-xl border border-red-200 p-3">
          <div className="text-2xl font-bold text-red-700">{overdue}</div>
          <div className="text-xs text-red-600">Overdue</div>
        </div>
        <div className="bg-white rounded-xl border border-green-200 p-3">
          <div className="text-2xl font-bold text-green-700">{completed}</div>
          <div className="text-xs text-green-600">Completed</div>
        </div>
      </div>

      {/* Search bar + status + actions */}
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <input className="input-field w-52 text-sm" placeholder="Search…" value={search}
          onChange={(e) => setSearch(e.target.value)} />
        <select className="input-field w-36 text-sm" value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="COMPLETED">Completed</option>
        </select>
        {activeFilterCount > 0 && (
          <button className="text-xs text-red-500 hover:text-red-700 font-medium"
            onClick={() => { setFilterClientId(''); setFilterTaskId(''); setFilterStatus(''); setFilterTypeId(''); setFilterPeriod(''); setDueFrom(''); setDueTo(''); }}>
            Clear filters ({activeFilterCount})
          </button>
        )}
        <div className="ml-auto flex gap-2 items-center">
          <button className="btn-secondary text-sm" onClick={() => setShowBulkEnroll(true)}>
            🪄 Bulk Enroll
          </button>
          {canEdit && (
            <button className="btn-secondary text-sm" onClick={() => setShowBulkExtend(true)}>
              📅 Extend Due Dates
            </button>
          )}
          {canEdit && (
            <button className="btn-secondary text-sm" onClick={openGstCheck} disabled={gstChecking}>
              {gstChecking ? 'Checking…' : '🔍 Check GST Filing'}
            </button>
          )}
          <button
            className="btn-secondary text-sm"
            onClick={() => exportToExcel(
              filteredItems.map((i) => ({
                'No.': i.complianceNumber,
                Type: i.complianceType.name,
                Frequency: i.complianceType.frequency,
                'GSTIN / Tax ID': i.taxId || '',
                'Task ID': i.task.taskId,
                'Task Name': i.task.taskName,
                Client: i.task.client.clientName,
                Period: i.periodLabel,
                'Due Date': fmtDateExcel(i.dueDate),
                'Extended Due': fmtDateExcel(i.extendedDueDate || ''),
                Status: i.status,
                'Completed On': fmtDateExcel(i.completedOn || ''),
                'GST Filing Status': i.gstFilingStatus || '',
                'GST Filed Date': i.gstFiledDate || '',
                Remarks: i.remarks || '',
                File: i.originalName || '',
              })),
              'Compliances',
              'Compliances'
            )}
          >
            ⬇ Export Excel
          </button>
          {isAdmin && (
            <button className="btn-primary text-sm" onClick={handleGenerate} disabled={generating}>
              {generating ? 'Generating…' : '⚡ Generate Now'}
            </button>
          )}
        </div>
      </div>

      {/* Bulk enroll wizard */}
      {showBulkEnroll && (
        <BulkEnrollComplianceWizard
          onClose={() => setShowBulkEnroll(false)}
          onComplete={() => load()}
        />
      )}

      {/* Bulk extend modal */}
      {showBulkExtend && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-3 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-purple-800">Extend Due Date — By Type & Period</h4>
            <button className="text-gray-400 hover:text-gray-600" onClick={() => setShowBulkExtend(false)}>✕</button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Compliance Type</label>
              <select className="input-field text-sm" value={bulkExtendTypeId} onChange={e => setBulkExtendTypeId(e.target.value)}>
                <option value="">— Select type —</option>
                {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Period</label>
              <select className="input-field text-sm" value={bulkExtendPeriod} onChange={e => setBulkExtendPeriod(e.target.value)}>
                <option value="">— Select period —</option>
                {periodOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">New Due Date</label>
              <input type="date" className="input-field text-sm" value={bulkExtendDate}
                onChange={e => setBulkExtendDate(e.target.value)} />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-purple-600">
              {bulkExtendTypeId && bulkExtendPeriod
                ? `${items.filter(i => i.complianceType.id === Number(bulkExtendTypeId) && i.periodLabel === bulkExtendPeriod).length} compliance(s) will be updated`
                : 'Select type and period to see matching count'}
            </span>
            <button className="btn-primary text-sm" onClick={handleBulkExtendSubmit}
              disabled={saving || !bulkExtendTypeId || !bulkExtendPeriod || !bulkExtendDate}>
              {saving ? 'Extending…' : 'Extend All'}
            </button>
          </div>
        </div>
      )}

      {/* GST filing check chooser */}
      {showGstCheck && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-3 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-indigo-800">Check GST Filing — Select Returns & Periods</h4>
            <button className="text-gray-400 hover:text-gray-600" onClick={() => setShowGstCheck(false)}>✕</button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Return Types</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {GST_TYPE_NAMES.map(n => {
                  const on = gstCheckTypes.includes(n);
                  return (
                    <button key={n} type="button"
                      onClick={() => setGstCheckTypes(on ? gstCheckTypes.filter(x => x !== n) : [...gstCheckTypes, n])}
                      className={`text-xs px-3 py-1 rounded-full border font-medium ${on ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'}`}>
                      {n}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="label">Periods (months)</label>
                <div className="flex gap-2 text-xs">
                  <button type="button" className="text-indigo-600 hover:underline" onClick={() => setGstCheckPeriods(gstPeriodOptions)}>All</button>
                  <button type="button" className="text-gray-500 hover:underline" onClick={() => setGstCheckPeriods([])}>None</button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-1 max-h-28 overflow-y-auto">
                {gstPeriodOptions.length === 0 ? (
                  <span className="text-xs text-gray-400">No GST periods available</span>
                ) : gstPeriodOptions.map(p => {
                  const on = gstCheckPeriods.includes(p);
                  return (
                    <button key={p} type="button"
                      onClick={() => setGstCheckPeriods(on ? gstCheckPeriods.filter(x => x !== p) : [...gstCheckPeriods, p])}
                      className={`text-xs px-2.5 py-1 rounded-full border font-medium ${on ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'}`}>
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-indigo-600">
              {gstCheckTypes.length === 0 || gstCheckPeriods.length === 0
                ? 'Select at least one return type and one period'
                : `${gstMatchCount} pending compliance(s) will be checked — already-filed are skipped`}
            </span>
            <button className="btn-primary text-sm" onClick={handleGstCheck}
              disabled={gstChecking || gstCheckTypes.length === 0 || gstCheckPeriods.length === 0}>
              {gstChecking ? 'Checking…' : 'Check Now'}
            </button>
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {selectedIds.length > 0 && canEdit && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-blue-800">{selectedIds.length} selected</span>
          <div className="flex gap-2 items-center">
            <select className="input-field text-sm py-1" value={bulkAction} onChange={e => setBulkAction(e.target.value)}>
              <option value="">— Action —</option>
              <option value="PENDING">Set Pending</option>
              <option value="IN_PROGRESS">Set In Progress</option>
              <option value="COMPLETED">Set Completed</option>
              {(isAdmin || isPartner) && <option value="DELETE">Delete</option>}
            </select>
            <button className="btn-primary text-sm py-1" onClick={handleBulkAction} disabled={!bulkAction}>Apply</button>
            <button className="btn-secondary text-sm py-1" onClick={() => { setSelectedIds([]); setBulkAction(''); }}>Clear</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center h-40 text-gray-400">Loading…</div>
        ) : filteredItems.length === 0 ? (
          <div className="flex items-center justify-center h-40 text-gray-400">No compliance instances found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="table-header w-8">
                  <input type="checkbox" className="w-4 h-4 rounded"
                    checked={filteredItems.length > 0 && selectedIds.length === filteredItems.length}
                    onChange={toggleSelectAll} />
                </th>
                <th className="table-header cursor-pointer select-none" onClick={() => handleSort('complianceNumber')}>No. <SI f="complianceNumber" /></th>
                <th className="table-header">
                  <ColumnFilter label="Type" options={typeOptions}
                    value={filterTypeId} onChange={setFilterTypeId}
                    sortable onSort={() => handleSort('type')} sortIcon={<SI f="type" />} />
                </th>
                <th className="table-header">
                  <ColumnFilter label="Task" options={taskOptions}
                    value={filterTaskId} onChange={setFilterTaskId}
                    sortable onSort={() => handleSort('task')} sortIcon={<SI f="task" />} />
                </th>
                <th className="table-header">
                  <ColumnFilter label="Client" options={clientOptions}
                    value={filterClientId} onChange={setFilterClientId}
                    sortable onSort={() => handleSort('client')} sortIcon={<SI f="client" />} />
                </th>
                <th className="table-header">
                  <ColumnFilter label="Period" options={periodOptions}
                    value={filterPeriod} onChange={setFilterPeriod}
                    sortable onSort={() => handleSort('period')} sortIcon={<SI f="period" />} />
                </th>
                <th className="table-header">
                  <DateRangeFilter
                    label="Due Date"
                    fromValue={dueFrom}
                    toValue={dueTo}
                    onChange={(f, t) => { setDueFrom(f); setDueTo(t); }}
                    sortable onSort={() => handleSort('dueDate')} sortIcon={<SI f="dueDate" />}
                  />
                </th>
                <th className="table-header cursor-pointer select-none" onClick={() => handleSort('status')}>Status <SI f="status" /></th>
                <th className="table-header cursor-pointer select-none" onClick={() => handleSort('gstFilingStatus')}>GST Filing <SI f="gstFilingStatus" /></th>
                <th className="table-header">File</th>
                <th className="table-header">Remarks</th>
                <th className="table-header"></th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => {
                const dueInfo = getDueStatus(item);
                const effectiveDue = getEffectiveDue(item);
                return (
                  <tr key={item.id} className={`border-b border-gray-50 hover:bg-gray-50 ${
                    isEscalated(item) ? 'bg-red-50/40' : ''
                  } ${selectedIds.includes(item.id) ? 'bg-blue-50' : ''}`}>
                    <td className="table-cell w-8">
                      <input type="checkbox" className="w-4 h-4 rounded"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => toggleSelect(item.id)} />
                    </td>
                    <td className="table-cell font-mono text-xs text-blue-700 font-semibold whitespace-nowrap">
                      {item.complianceNumber}
                    </td>
                    <td className="table-cell">
                      <span className="text-xs font-medium text-gray-700" title={item.complianceType.name}>{item.complianceType.name}</span>
                      <div className="text-xs text-gray-400">{item.complianceType.frequency}</div>
                      {item.taxId && <div className="text-[11px] font-mono text-indigo-600" title="GSTIN / Tax ID">{item.taxId}</div>}
                    </td>
                    <td className="table-cell text-xs">
                      <span className="text-blue-600 font-medium">{item.task.taskId}</span>
                      <div className="text-gray-400 truncate max-w-[100px]" title={item.task.taskName}>{item.task.taskName}</div>
                    </td>
                    <td className="table-cell text-xs text-gray-600 whitespace-nowrap" title={item.task.client.clientName}>
                      {item.task.client.clientName}
                    </td>
                    <td className="table-cell text-xs font-medium text-gray-800">
                      {item.periodLabel}
                    </td>
                    <td className="table-cell text-xs whitespace-nowrap relative">
                      <div>{fmtDate(effectiveDue)}</div>
                      {item.extendedDueDate && (
                        <div className="text-xs text-purple-600" title={`Original: ${fmtDate(item.dueDate)}`}>
                          Extended
                          {canEdit && <button className="ml-1 text-red-400 hover:text-red-600" onClick={() => handleClearExtension(item)} title="Remove extension">✕</button>}
                        </div>
                      )}
                      {dueInfo && <div className={`text-xs ${dueInfo.cls}`}>{dueInfo.label}</div>}
                      {canEdit && item.status !== 'COMPLETED' && (
                        <button className="text-xs text-purple-500 hover:text-purple-700 font-medium mt-0.5"
                          onClick={() => { setExtendingId(item.id); setExtendDate(item.extendedDueDate?.slice(0,10) || item.dueDate?.slice(0,10) || ''); }}>
                          {item.extendedDueDate ? 'Change' : 'Extend'}
                        </button>
                      )}
                      {extendingId === item.id && (
                        <div className="absolute z-20 top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-52">
                          <label className="text-xs font-medium text-gray-700 block mb-1">Extended Due Date</label>
                          <input type="date" className="input-field text-xs w-full" value={extendDate}
                            onChange={(e) => setExtendDate(e.target.value)} autoFocus />
                          <div className="flex gap-1.5 mt-2 justify-end">
                            <button className="btn-secondary text-xs py-0.5 px-2" onClick={() => setExtendingId(null)}>Cancel</button>
                            <button className="btn-primary text-xs py-0.5 px-2" onClick={handleExtendSubmit} disabled={saving || !extendDate}>Save</button>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="table-cell">
                      {canEdit && item.status !== 'COMPLETED' ? (
                        <select
                          className={`text-xs px-2 py-1 rounded-full border font-medium ${STATUS_COLORS[item.status] || ''}`}
                          value={item.status}
                          onChange={(e) => handleStatusChange(item, e.target.value)}
                        >
                          <option value="PENDING">Pending</option>
                          <option value="IN_PROGRESS">In Progress</option>
                          <option value="COMPLETED">Completed</option>
                        </select>
                      ) : (
                        <span className={`text-xs px-2 py-1 rounded-full border font-medium ${STATUS_COLORS[item.status] || ''}`}>
                          {item.status.replace(/_/g, ' ')}
                        </span>
                      )}
                    </td>
                    <td className="table-cell text-xs whitespace-nowrap">
                      {GST_TYPE_NAMES.includes(item.complianceType.name) ? (
                        <div>
                          {item.gstFilingStatus === 'Filed' ? (
                            <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200 font-medium">
                              ✓ Filed
                            </span>
                          ) : item.gstEscalationLevel ? (
                            <span className={`px-2 py-0.5 rounded-full border font-medium ${ESCALATION_COLORS[item.gstEscalationLevel] || ''}`}>
                              {ESCALATION_LABELS[item.gstEscalationLevel] || item.gstEscalationLevel}
                            </span>
                          ) : item.gstFilingStatus === 'Not Filed' ? (
                            <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-200 font-medium">
                              Not Filed
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                          {item.gstFiledDate && (
                            <div className="text-gray-400 mt-0.5">{item.gstFiledDate}</div>
                          )}
                          {item.gstLastCheckedAt && (
                            <div className="text-gray-300 mt-0.5" title={`Last checked: ${new Date(item.gstLastCheckedAt).toLocaleString('en-IN')}`}>
                              Chk: {fmtDate(item.gstLastCheckedAt)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-300">N/A</span>
                      )}
                    </td>
                    <td className="table-cell">
                      {item.fileName ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          <a href={uploadsUrl(item.fileName)} target="_blank" rel="noreferrer"
                            className="text-xs text-blue-600 hover:underline" title={item.originalName}>
                            📄 {item.originalName && item.originalName.length > 14 ? item.originalName.slice(0, 12) + '…' : item.originalName}
                          </a>
                          <span className="text-gray-400 text-xs">{fmtSize(item.fileSize)}</span>
                          {canEdit && (
                            <button className="text-red-400 hover:text-red-600 text-xs" onClick={() => handleDeleteFile(item)}>✕</button>
                          )}
                        </div>
                      ) : canEdit ? (
                        <label className={`cursor-pointer text-xs text-indigo-600 hover:text-indigo-800 font-medium ${uploadingId === item.id ? 'opacity-50 pointer-events-none' : ''}`}>
                          {uploadingId === item.id ? 'Uploading…' : '+ Upload'}
                          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.xls"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(item, f); e.target.value = ''; }} />
                        </label>
                      ) : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                    <td className="table-cell text-xs max-w-[150px]">
                      {editingId === item.id ? (
                        <div className="flex gap-1">
                          <input className="input-field text-xs py-1 flex-1" value={editRemarks}
                            onChange={(e) => setEditRemarks(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveRemarks(item); if (e.key === 'Escape') setEditingId(null); }}
                            autoFocus />
                          <button className="text-green-600 text-xs font-medium" onClick={() => handleSaveRemarks(item)} disabled={saving}>✓</button>
                          <button className="text-gray-400 text-xs" onClick={() => setEditingId(null)}>✕</button>
                        </div>
                      ) : (
                        <span className="text-gray-500 cursor-pointer hover:text-gray-700"
                          onClick={() => { if (canEdit) { setEditingId(item.id); setEditRemarks(item.remarks || ''); } }}>
                          {item.remarks || <span className="text-gray-300">—</span>}
                        </span>
                      )}
                    </td>
                    <td className="table-cell">
                      {(isAdmin || isPartner) && (
                        <button className="text-xs text-red-500 hover:text-red-700" onClick={() => handleDelete(item)}>Del</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Compliances;
